// icons
import { iconGlyphs, type Icons } from '@/components/atoms/Icon/icons';

/**
 * FALLBACK_ICON renders when a name cannot be resolved. It is deliberately
 * innocuous so a bad record degrades to a neutral glyph rather than a gap in
 * the layout.
 */
export const FALLBACK_ICON: Icons = 'star';

/**
 * iconAliases maps legacy FontAwesome names onto the Phosphor-backed names the
 * design system now ships.
 *
 * Icon names reach the front end as free text from three places we do not
 * control at build time: `action_plan_item.icon` and `action_plan_category.icon`
 * in the database, and the icon field on Sanity documents. Those values were
 * authored against the FontAwesome catalogue, so they include Pro-only names,
 * FontAwesome 4/5 names and short synonyms that have no registry key of the
 * same name. Rather than migrate every row and CMS document up front, we
 * translate on read.
 *
 * An alias translates a name; it never substitutes a different drawing. When a
 * legacy name has no glyph of the same shape in the registry, add the glyph
 * rather than aliasing to a near miss, otherwise the record renders wrong and
 * the dev warning that would have flagged it never fires.
 *
 * Keys are normalised names (see `normaliseIconName`). Add to this table when a
 * new legacy name shows up in `warnUnknownIcon`.
 */
const iconAliases: Record<string, Icons> = {
  // arrows and chevrons
  'angle-down': 'chevron-down',
  'angle-left': 'chevron-left',
  'angle-right': 'chevron-right',
  'angle-up': 'chevron-up',
  'angle-double-down': 'chevrons-down',
  'angle-double-left': 'chevrons-left',
  'angle-double-right': 'chevrons-right',
  'angle-double-up': 'chevrons-up',
  'chevron-double-down': 'chevrons-down',
  'chevron-double-left': 'chevrons-left',
  'chevron-double-right': 'chevrons-right',
  'chevron-double-up': 'chevrons-up',
  'arrow-left': 'long-arrow-left',
  'arrow-right': 'long-arrow-right',
  'long-arrow-alt-left': 'long-arrow-left',
  'long-arrow-alt-right': 'long-arrow-right',
  'sign-out': 'arrow-right-from-bracket',
  'sign-out-alt': 'arrow-right-from-bracket',
  logout: 'arrow-right-from-bracket',

  // actions
  cog: 'gear',
  cogs: 'gear',
  settings: 'gear',
  'pencil-alt': 'pen',
  'pen-to-square': 'pen-field',
  'trash-alt': 'trash',
  'trash-can': 'trash',
  delete: 'trash',
  close: 'xmark',
  remove: 'xmark',
  'times-circle': 'circle-xmark',
  'plus-circle': 'circle-plus',
  'minus-circle': 'circle-minus',
  redo: 'rotate-right',
  'redo-alt': 'rotate-right',
  refresh: 'rotate-right',
  sync: 'arrows-rotate',
  'sync-alt': 'arrows-rotate',
  sliders: 'sliders-h',
  'ellipsis-h': 'ellipsis',
  'ellipsis-v': 'ellipsis-vertical',
  'list-ul': 'list',
  tasks: 'list-check',
  'external-link': 'arrow-up-right-from-square',
  'external-link-alt': 'arrow-up-right-from-square',
  'up-right-from-square': 'arrow-up-right-from-square',
  unlink: 'link-slash',
  'chain-broken': 'link-slash',
  'paper-clip': 'paperclip',
  attach: 'paperclip',

  // upload and download
  'cloud-upload': 'cloud-arrow-up',
  'cloud-upload-alt': 'cloud-arrow-up',
  'file-upload': 'file-arrow-up',
  'cloud-download': 'cloud-arrow-down',
  'file-download': 'file-arrow-down',
  save: 'floppy-disk',

  // status
  warning: 'exclamation-triangle',
  'triangle-exclamation': 'exclamation-triangle',
  'circle-exclamation': 'exclamation-circle',
  'circle-info': 'info-circle',
  info: 'info-circle',
  'circle-check': 'check-circle',
  question: 'question-circle',
  'circle-xmark-solid': 'circle-xmark',

  // people
  'user-circle': 'circle-user',
  'user-alt': 'user',
  'user-md': 'user-nurse',
  'user-doctor': 'user-nurse',
  stethoscope: 'user-nurse',
  'user-friends': 'user-group',
  'users-alt': 'users',
  'user-tie': 'user',
  walking: 'person-walking',
  'wheelchair-alt': 'wheelchair',

  // communication
  comment: 'message',
  'paper-plane': 'paper-plane-top',
  send: 'paper-plane-top',
  'envelope-open': 'envelope-open-text',
  mail: 'envelope',
  'phone-alt': 'phone',
  'headphones-alt': 'headphones',
  'microphone-alt': 'microphone',
  mic: 'microphone',

  // documents
  'file-alt': 'file-lines',
  'file-text': 'file-lines',
  'file-signature': 'file-contract',
  'sticky-note': 'note-sticky',
  'notes-medical': 'memo',
  notes: 'memo',
  printer: 'print',
  'calendar-alt': 'calendar-days',

  // health and care
  heartbeat: 'heart-pulse',
  'heart-rate': 'heart-pulse',
  'first-aid': 'kit-medical',
  medkit: 'kit-medical',
  'briefcase-medical': 'kit-medical',
  'hand-holding-medical': 'hand-holding-heart',
  'hands-helping-alt': 'hands-helping',
  'balance-scale': 'scale-balanced',

  // money
  gbp: 'sterling-sign',
  pound: 'sterling-sign',
  sterling: 'sterling-sign',
  'money-bill': 'money-bills',
  'money-bill-wave': 'money-bills',

  // places and things
  'map-pin': 'map-marker-alt',
  location: 'map-marker-alt',
  'search-location': 'magnifying-glass-location',
  'home-alt': 'house',
  'home-lg': 'house',
  sofa: 'couch',
  truck: 'truck-fast',
  shield: 'shield-alt',
  key: 'key-skeleton',
  photo: 'image',
  picture: 'image',
  'video-camera': 'video',
  'play-circle': 'circle-play',
  'pause-circle': 'circle-pause',
  'stop-circle': 'circle-stop',
  magic: 'wand-magic-sparkles',
  stars: 'sparkles',
  'party-popper': 'party-horn',
  celebration: 'party-horn',
  'face-smile': 'smile',
  'smile-beam': 'smile',
  grin: 'smile',
  network: 'sitemap',
  directions: 'route',
};

/**
 * isModifierClass matches the FontAwesome classes that say how to draw an icon
 * rather than which one: the family classes, both the version 5 short forms
 * (`fas`, `fal`) and the version 6 long forms (`fa-solid`, `fa-light`), and the
 * sizing, animation, rotation and layout utilities (`fa-fw`, `fa-2x`, `fa-spin`).
 */
const isModifierClass = (token: string): boolean =>
  /^fa[slrbdt]?$/.test(token) ||
  /^fa-(solid|regular|light|thin|duotone|brands|sharp|sharp-solid|classic|fw|ul|li|border|inverse|spin|spin-pulse|spin-reverse|pulse|beat|beat-fade|fade|bounce|shake|flip|flip-horizontal|flip-vertical|flip-both|rotate-\d+|rotate-by|pull-left|pull-right|stack|stack-1x|stack-2x|2xs|xs|sm|lg|xl|2xl|\d+x)$/.test(
    token,
  );

/**
 * normaliseIconName reduces the many shapes an icon name arrives in — CSS class
 * strings (`fas fa-heart fa-fw`), prefixed names (`fa-heart`), snake case,
 * camel case and stray whitespace — down to the kebab-case form used as a
 * registry key.
 */
export const normaliseIconName = (raw: string): string => {
  const tokens = raw
    // split camel case before lowercasing, so `handHoldingHeart` survives
    .replace(/([a-z0-9])([A-Z])/g, '$1-$2')
    .trim()
    .toLowerCase()
    .split(/\s+/);

  // a CSS class string (`fas fa-heart fa-fw`) names the icon in its `fa-` token
  // and may carry modifier classes in any order, whereas a plain name
  // (`hand holding heart`) is the whole string
  const isClassString = tokens.some(
    (token) => isModifierClass(token) || token.startsWith('fa-'),
  );

  const name = isClassString
    ? (tokens.find(
        (token) => token.startsWith('fa-') && !isModifierClass(token),
      ) ??
      tokens.filter((token) => !isModifierClass(token)).pop() ??
      '')
    : tokens.join('-');

  return name
    .replace(/^fa-/, '')
    .replace(/[\s_]+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '');
};

const isKnownIcon = (name: string): name is Icons => name in iconGlyphs;

const seenUnknown = new Set<string>();

/**
 * warnUnknownIcon reports an unresolvable name in development only.
 *
 * This is deliberately not wired to Sentry. The design system carries no
 * telemetry dependency of its own, and icon names come from a bounded, listable
 * set of records — `action_plan_item.icon`, `action_plan_category.icon` and the
 * Sanity documents — so querying those finds every bad value at once rather
 * than waiting for one to be rendered in production. The fallback renders and
 * the page stays usable meanwhile.
 *
 * Each distinct name warns once, so a list rendering the same broken record
 * repeatedly does not flood the console.
 */
const warnUnknownIcon = (name: string, raw: string) => {
  if (process.env.NODE_ENV === 'production') return;
  if (seenUnknown.has(name)) return;

  seenUnknown.add(name);
  // eslint-disable-next-line no-console
  console.warn(
    `[llama] unknown icon "${raw}" (normalised to "${name}") — falling back to "${FALLBACK_ICON}". Add it to iconAliases or the icon registry.`,
  );
};

/**
 * resolveIcon translates an arbitrary icon name — typically an action plan or
 * Sanity record written against the old FontAwesome catalogue — into a name the
 * Icon component can render. Unresolvable names fall back to `FALLBACK_ICON`.
 */
export const resolveIcon = (raw: string | null | undefined): Icons => {
  if (!raw) return FALLBACK_ICON;

  const name = normaliseIconName(raw);
  if (isKnownIcon(name)) return name;

  const alias = iconAliases[name];
  if (alias) return alias;

  warnUnknownIcon(name, raw);

  return FALLBACK_ICON;
};
